import { Outlet, Navigate, useLocation, useMatches } from "react-router";
import type { User } from "@/types/api";
import { useAuth } from "@/context/AuthContext";
import { MEMBER_CONTINUE_REGISTER_PATH, memberNeedsProfileWizard } from "@/constants/memberContinueRegisterQuestions";
import {
  shouldBypassEmailVerificationGate,
  shouldBypassPhoneVerificationGate,
} from "@/constants/verificationGate";
import AIConcierge from "./AIConcierge";
import { ChatRealtimeBridge } from "./ChatRealtimeBridge";
import { ChatIncomingMessageNotifier } from "./ChatIncomingMessageNotifier";
import { FriendRequestNotifier } from "./FriendRequestNotifier";
import { ThemeProvider } from "./ThemeContext";
import ScrollToTop from "./ScrollToTop";
import { Toaster } from "./ui/sonner";
import ViewerCurrencyInit from "./ViewerCurrencyInit";
import { MemberOnlyDialog } from "./MemberOnlyDialog";
import { AddVenueMemberDialog } from "./AddVenueMemberDialog";
import { isAdminEmbed } from "@/lib/adminEmbed";

function sessionAccountType(user: User | null): User["accountType"] | undefined {
  if (!user) return undefined;
  const u = user as User & { account_type?: string };
  const t = user.accountType ?? u.account_type;
  return t === "member" || t === "venue" ? t : undefined;
}

/** Where an authenticated member must be before using the app (strict order). */
function getMemberMandatoryPath(user: User | null): string | null {
  // Admin editing from the dashboard: never push them through the member's own onboarding gates.
  if (!user || sessionAccountType(user) !== "member" || isAdminEmbed) return null;
  if (!user.emailVerifiedAt && !shouldBypassEmailVerificationGate()) return "/verify-email";
  if (user.phone && !user.phoneVerifiedAt && !shouldBypassPhoneVerificationGate()) return "/verify-phone";
  if (memberNeedsProfileWizard(user)) return MEMBER_CONTINUE_REGISTER_PATH;
  return null;
}

function isAuthFlowExemptPath(pathname: string): boolean {
  if (pathname === "/login") return true;
  if (pathname === "/forgot-password") return true;
  if (pathname === "/reset-password") return true;
  if (/^\/reset-password\/.+/.test(pathname)) return true;
  if (pathname === "/verify") return true;
  if (/^\/auth\/[^/]+\/callback$/.test(pathname)) return true;
  return false;
}

/** Venue onboarding/edit flows — must not be interrupted by member-only gates on refresh. */
function isVenueOwnerFlowPath(pathname: string): boolean {
  return (
    pathname === "/add-venue" ||
    pathname === "/for-venues" ||
    pathname === "/venue-dashboard" ||
    pathname === "/account-settings" ||
    pathname === "/steps" ||
    pathname.startsWith("/steps/")
  );
}

const VENUE_STEPS_PATH = "/steps/venue-information";

/** Pages a venue account may still open; everything else (home included) sends them to their venue steps. */
function isVenueAccountAllowedPath(pathname: string): boolean {
  return (
    isVenueOwnerFlowPath(pathname) ||
    isAuthFlowExemptPath(pathname) ||
    ["/verify-email", "/verify-phone", "/contact", "/privacy", "/terms", "/cookies", "/community-guidelines"].includes(pathname)
  );
}

export default function RootLayout() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  const matches = useMatches();
  // Venue profile pages are let through here; VenueDetail itself bounces non-owners.
  const isVenueDetailRoute = matches.some((m) => m.id.startsWith("venue-detail"));
  const venueMustStayInSteps =
    isAuthenticated &&
    sessionAccountType(user) === "venue" &&
    !isVenueDetailRoute &&
    !isVenueAccountAllowedPath(location.pathname);

  const mandatoryMemberPath = isAuthenticated
    ? venueMustStayInSteps ? VENUE_STEPS_PATH : getMemberMandatoryPath(user)
    : null;

  const mustFinishMemberStep =
    mandatoryMemberPath !== null &&
    location.pathname !== mandatoryMemberPath &&
    (venueMustStayInSteps ||
      (!isAuthFlowExemptPath(location.pathname) && !isVenueOwnerFlowPath(location.pathname)));

  return (
    <ThemeProvider>
      <ScrollToTop />
      <ViewerCurrencyInit />
      {mustFinishMemberStep ? (
        <Navigate
          to={mandatoryMemberPath!}
          replace
          state={{ from: `${location.pathname}${location.search}` }}
        />
      ) : (
        <Outlet />
      )}
      {!isAdminEmbed && (
        <>
          <ChatRealtimeBridge />
          <ChatIncomingMessageNotifier />
          <FriendRequestNotifier />
          <AIConcierge />
          <MemberOnlyDialog />
          <AddVenueMemberDialog />
        </>
      )}
      <Toaster />
    </ThemeProvider>
  );
}
